export interface PublicConferenceDate {
  start_date?: string | null;
  end_date?: string | null;
  date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  timezone?: string | null;
  is_placeholder?: boolean | null;
}

export interface PublicConferenceRow {
  id: string;
  region_id: string | null;
  conference_name: string;
  conference_date: string | null;
  conference_dates: PublicConferenceDate[] | null;
  location: string | null;
  flyer_url: string | null;
  is_active: boolean | null;
  status: string | null;
  slogan?: string | null;
  description?: string | null;
  facilitators?: Array<{ name?: string; title?: string } | string> | null;
  contact_phone?: string | null;
  contact_name?: string | null;
}

export interface PublicKitEventRow {
  region_id: string | null;
  title?: string | null;
  theme?: string | null;
  scripture?: string | null;
  venue_name: string | null;
  venue_address?: string | null;
  city: string | null;
  state: string | null;
  start_date: string | null;
  end_date: string | null;
  start_time?: string | null;
  end_time?: string | null;
  conference_year: number | null;
  flyer_url: string | null;
  flyer_approved: boolean | null;
  status: string | null;
  coordinator_name?: string | null;
  coordinator_email?: string | null;
  coordinator_phone?: string | null;
  registration_url?: string | null;
  speaker_name?: string | null;
  speaker_title?: string | null;
  updated_at?: string | null;
  hotel_guide_url?: string | null;
  hotel_guide_filename?: string | null;
  hotel_guide_size?: number | null;
  hotel_guide_uploaded_at?: string | null;
}

export interface ResolvedPublicConference {
  slug: string;
  displayRegionName: string;
  title: string | null;
  start: string | null;
  end: string | null;
  startTime: string | null;
  endTime: string | null;
  timezone: string | null;
  isPlaceholder: boolean;
  timeLabel: string | null;
  venue: string | null;
  venueName: string | null;
  venueAddress: string | null;
  cityState: string | null;
  flyer: string;
  theme: string;
  scripture: string;
  topic: string;
  speakers: string[];
  contactPhone: string | null;
  contactEmail: string | null;
  coordinatorName: string | null;
  coordinatorPhone: string | null;
  registrationUrl: string | null;
  heroImage: string | null;
  updatedAt: string | null;
  status: string | null;
  status: string | null;
}

export const SHARED_2026_FLYER_URL = "/flyers/southern-2026-qr.png";
export const EASTERN_2026_FLYER_URL = "/flyers/eastern-2026.jpg?v=20260723";
export const MIDWEST_2026_FLYER_URL =
  "/__l5e/assets-v1/49ae7994-8508-46b8-a0c1-74181524f661/midwest-2026.png";
export const DENVER_2026_FLYER_URL =
  "https://ofuesi.org/__l5e/assets-v1/9c653294-9057-4cb8-9859-d8fde3c397c3/denver-2026.jpg";
export const NORTHWEST_2026_FLYER_URL =
  "/__l5e/assets-v1/bb7cc363-1875-42f0-8782-23ba49d0e051/ofuesi-northwest-2026-flyer-v2.png";

const HERO_BY_SLUG: Record<string, string> = {
  southern: "/flyers/southern-2026-backdrop-qr.png",
};

const COORDINATORS: Record<string, { name: string; phone: string }> = {
  southern: { name: "Anand Mikkili", phone: "+1 832-758-1817" },
};


const FLYER_BY_SLUG: Record<string, string> = {
  southern: "/flyers/southern-2026-qr.png",
  southeast: "/flyers/southeast-2026.jpg",
  eastern: EASTERN_2026_FLYER_URL,
  midwest: MIDWEST_2026_FLYER_URL,
  northwest: NORTHWEST_2026_FLYER_URL,
  southwest: DENVER_2026_FLYER_URL,
};

export const getFlyerForRegion = (regionName: string): string => {
  const slug = regionName.toLowerCase().replace(/region/g, "").replace(/[^a-z0-9]+/g, "").trim();
  return FLYER_BY_SLUG[slug] || SHARED_2026_FLYER_URL;
};
export const CONFERENCE_2026_THEME = "Breathe Again";
export const CONFERENCE_2026_SCRIPTURE = "Valley of Dry Bones (Ezekiel 37)";
export const CONFERENCE_2026_TOPIC_POINTS = [
  "Speaking life into dead situations",
  "The Holy Spirit reviving",
  "Unity of the body coming together",
];
export const CONFERENCE_2026_TOPIC = CONFERENCE_2026_TOPIC_POINTS.join(" · ");

export const parseConferenceDateValue = (value?: string | null) => {
  if (!value) return null;
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const ordinal = (day: number) => {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

const speakerOverrides: Record<string, string[]> = {
  southern: ["Dr. C.S. Dutt"],
  southeast: ["Bro. Sudhakar Mondithoka", "Sis. Shanti"],
  eastern: ["Bro. Sudhakar Mondithoka", "Sis. Shanti"],
  northwest: ["Bro. Ranganadham", "Sis. Ratna"],
  midwest: ["Bro. Martin Alphonse"],
  southwest: [],
};


export const toConferenceSlug = (value: string) =>
  value.toLowerCase().replace(/region/g, "").replace(/[^a-z0-9]+/g, "").trim();

export const getConferenceDisplayRegionName = (value: string) => {
  if (/north\s*west/i.test(value)) return "Pacific Northwest";
  if (/south\s*west/i.test(value)) return "Denver OFUESI Conference";
  return value;
};

export const formatSpeakerNames = (
  regionName: string,
  facilitators?: PublicConferenceRow["facilitators"],
) => {
  const slug = toConferenceSlug(regionName);
  const override = speakerOverrides[slug];
  if (override) return override;

  return Array.isArray(facilitators)
    ? facilitators
        .map((entry) => (typeof entry === "string" ? entry : entry?.name))
        .filter((name): name is string => Boolean(name && name.trim()))
    : [];
};

const sanitizeVenue = (value?: string | null) => {
  if (!value) return null;
  if (/venue\s+[-—]?\s*to be announced/i.test(value)) return null;
  if (/venue tbd/i.test(value)) return null;
  return value;
};

const TZ_BY_STATE: Record<string, string> = {
  WA: "PT", OR: "PT", CA: "PT", NV: "PT", AK: "AKT",
  ID: "MT", MT: "MT", WY: "MT", CO: "MT", UT: "MT", AZ: "MT", NM: "MT",
  TX: "CT", IL: "CT", MN: "CT", WI: "CT", IA: "CT", MO: "CT", OK: "CT", KS: "CT", NE: "CT", LA: "CT", AR: "CT", TN: "CT", AL: "CT", MS: "CT",
};

export const timezoneLabelForState = (state?: string | null): string | null =>
  state ? TZ_BY_STATE[state.trim().toUpperCase()] || "ET" : null;

const to12Hour = (value?: string | null): string | null => {
  if (!value) return null;
  const m = value.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return value.trim();
  let hour = Number(m[1]);
  const minutes = m[2];
  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return minutes === "00" ? `${hour} ${suffix}` : `${hour}:${minutes} ${suffix}`;
};

export const formatConferenceTimeRange = (
  start?: string | null,
  end?: string | null,
  timezone?: string | null,
): string | null => {
  const s = to12Hour(start);
  if (!s) return null;
  const e = to12Hour(end);
  const tz = timezone ? ` ${timezone}` : "";
  return e ? `${s} – ${e}${tz}` : `${s}${tz}`;
};

export const formatConferenceDateRange = (
  start?: string | null,
  end?: string | null,
  style: "short" | "long" = "short",
) => {
  if (!start) return "Dates to be announced";

  const s = parseConferenceDateValue(start);
  const e = parseConferenceDateValue(end);
  if (!s) return "Dates to be announced";

  if (!e || Number.isNaN(e.getTime()) || s.toDateString() === e.toDateString()) {
    if (style === "long") {
      return `${s.toLocaleDateString(undefined, { month: "long" })} ${ordinal(s.getDate())}, ${s.getFullYear()}`;
    }
    return s.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }

  if (style === "long") {
    if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
      return `${s.toLocaleDateString(undefined, { month: "long" })} ${ordinal(s.getDate())}–${ordinal(e.getDate())}, ${e.getFullYear()}`;
    }
    return `${s.toLocaleDateString(undefined, { month: "long" })} ${ordinal(s.getDate())}, ${s.getFullYear()} – ${e.toLocaleDateString(undefined, { month: "long" })} ${ordinal(e.getDate())}, ${e.getFullYear()}`;
  }

  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })}–${e.getDate()}, ${e.getFullYear()}`;
  }

  return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
};

export const resolvePublicConference = ({
  region,
  conferences,
  kitEvents,
}: {
  region: { id: string; name: string; conference_start_date?: string | null };
  conferences: PublicConferenceRow[];
  kitEvents: PublicKitEventRow[];
}): ResolvedPublicConference => {
  const regionConferences = conferences.filter((conference) => conference.region_id === region.id);
  const conference = regionConferences.find((entry) => /2026/.test(entry.conference_name || "")) || regionConferences[0] || null;
  const kitEvent = kitEvents.find((entry) => entry.region_id === region.id) || null;
  const primaryRange = conference?.conference_dates?.[0];

  const start =
    primaryRange?.start_date ||
    primaryRange?.date ||
    kitEvent?.start_date ||
    conference?.conference_date ||
    region.conference_start_date ||
    null;

  const end = primaryRange?.end_date || kitEvent?.end_date || null;
  const isPlaceholder = primaryRange?.is_placeholder === true;
  const startTime = primaryRange?.start_time || kitEvent?.start_time || null;
  const endTime = primaryRange?.end_time || kitEvent?.end_time || null;
  const timezone = primaryRange?.timezone || timezoneLabelForState(kitEvent?.state) || null;
  const timeLabel = formatConferenceTimeRange(startTime, endTime, timezone);

  const slug = toConferenceSlug(region.name);
  const cityState = [kitEvent?.city, kitEvent?.state].filter(Boolean).join(", ") || null;
  const venueName = sanitizeVenue(kitEvent?.venue_name || null);
  const venueAddress = sanitizeVenue(kitEvent?.venue_address || null);
  const venue =
    [venueName, venueAddress, cityState].filter(Boolean).join(" · ") ||
    sanitizeVenue(conference?.location || null);

  const kitSpeakers = [kitEvent?.speaker_name].filter((n): n is string => Boolean(n && n.trim()));
  const dbSpeakers = Array.isArray(conference?.facilitators)
    ? conference!.facilitators!
        .map((entry) => (typeof entry === "string" ? entry : entry?.name))
        .filter((name): name is string => Boolean(name && name.trim()))
    : [];
  const speakers = dbSpeakers.length ? dbSpeakers : (kitSpeakers.length ? kitSpeakers : formatSpeakerNames(region.name, conference?.facilitators));

  return {
    slug,
    displayRegionName: getConferenceDisplayRegionName(region.name),
    title: kitEvent?.title || conference?.conference_name || null,
    start,
    end,
    startTime,
    endTime,
    timezone,
    isPlaceholder,
    timeLabel,
    venue,
    venueName,
    venueAddress,
    cityState,
    flyer: slug === "southern" ? FLYER_BY_SLUG.southern : (kitEvent?.flyer_url || conference?.flyer_url || getFlyerForRegion(region.name)),
    theme: (kitEvent?.theme && kitEvent.theme.trim()) || CONFERENCE_2026_THEME,
    scripture: (kitEvent?.scripture && kitEvent.scripture.trim()) || CONFERENCE_2026_SCRIPTURE,
    topic: conference?.description || CONFERENCE_2026_TOPIC,
    speakers,
    contactPhone: kitEvent?.coordinator_phone || conference?.contact_phone || COORDINATORS[slug]?.phone || null,
    contactEmail: kitEvent?.coordinator_email || null,
    coordinatorName: kitEvent?.coordinator_name || conference?.contact_name || COORDINATORS[slug]?.name || null,
    coordinatorPhone: kitEvent?.coordinator_phone || COORDINATORS[slug]?.phone || null,
    registrationUrl: kitEvent?.registration_url || null,
    heroImage: HERO_BY_SLUG[slug] || null,
    updatedAt: kitEvent?.updated_at || null,
    status: kitEvent?.status || conference?.status || null,
  };
};